import type { UnavailableTabModel } from './index';
import { signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { toDisposable } from '@thinkalpha/ui-core/toDisposable';
import { inject, injectable } from 'src/features/ioc';
import type { SubscriptionPromptModel } from 'src/models/SubscriptionPromptModel';
import type { RootState } from 'src/store';
import { updateTabName } from 'src/store/actions/tab';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class UnavailableTabModelImpl implements UnavailableTabModel {
    readonly #disposableStack = new DisposableStack();
    readonly #isEditing = signal(false);
    readonly #editedName = signal('');
    readonly #tabNameSignal = signal('');
    #tabKey?: string;
    #storeUnsubscribe: DisposableBox;

    readonly isEditing: ReadonlySignal<boolean> = this.#isEditing;
    readonly editedName: ReadonlySignal<string> = this.#editedName;
    readonly tabName: ReadonlySignal<string> = this.#tabNameSignal;
    readonly isSubscriptionUser: ReadonlySignal<boolean>;

    constructor(
        @inject('SubscriptionPromptModel') private readonly promptModel: SubscriptionPromptModel,
        @inject('Store') private readonly store: ReactBindings['Store'],
    ) {
        this.isSubscriptionUser = this.promptModel.isSubscriptionUser;

        this.#storeUnsubscribe = this.#disposableStack.use(new DisposableBox());
    }

    init(tabKey: string): void {
        if (this.#tabKey === tabKey) return;
        this.#tabKey = tabKey;

        const selectTabName = (state: RootState) => state.tab.byId[tabKey]?.name ?? '';
        const initialName = selectTabName(this.store.getState());
        this.#tabNameSignal.value = initialName;
        this.#editedName.value = initialName;

        this.#storeUnsubscribe.next(() =>
            toDisposable(
                this.store.subscribeSelector(selectTabName).handle((name) => {
                    this.#tabNameSignal.value = name;
                    // Don't clobber in-progress edits
                    if (!this.#isEditing.value) {
                        this.#editedName.value = name;
                    }
                }),
            ),
        );
    }

    startEditing(): void {
        this.#editedName.value = this.#tabNameSignal.value;
        this.#isEditing.value = true;
    }

    setEditedName(name: string): void {
        this.#editedName.value = name;
    }

    applyEdit(): void {
        const name = this.#editedName.value;
        if (this.#tabKey && name !== this.#tabNameSignal.value) {
            const tab = this.store.getState().tab.byId[this.#tabKey];
            if (tab) {
                this.store.dispatch(updateTabName(tab.id, name));
            }
        }
        this.#isEditing.value = false;
    }

    cancelEdit(): void {
        this.#isEditing.value = false;
        this.#editedName.value = this.#tabNameSignal.value;
    }

    openSubscriptionSetup(): void {
        this.promptModel.openPlansOrBilling('locked_widget', 'select-plan');
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
